/**
 * Home — the app's front door (FOUNDATION-owned). A calm dashboard: a greeting,
 * the user's active trips with packed progress (the thing they most likely came
 * to do), and quick links into the Library and Activities.
 */
import { useMe } from '@/api/me';
import { useTripItems, useTrips } from '@/api/trips';
import { Skeleton } from '@/components/library/ui/Skeleton';
import { tripProgress } from '@/components/trips/tripProgress';
import { Progress } from '@/components/ui/Progress';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { Trip } from '@ultimate-checklist/shared';
import { ArrowRight, ListChecks, Luggage, Plus, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/')({
  component: Home,
});

function firstName(displayName: string | null, email: string): string {
  const fromName = displayName?.trim().split(/\s+/)[0];
  if (fromName) {
    return fromName;
  }
  return email.split('@')[0] ?? 'there';
}

function Home() {
  useDocumentTitle('Home');
  const me = useMe();
  const trips = useTrips('active');
  const greeting = me.data ? firstName(me.data.displayName, me.data.email) : null;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-10">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-3xl font-semibold">{greeting ? `Hi, ${greeting}` : 'Welcome back'}</h1>
        <p className="text-[var(--muted-foreground)]">
          Pick up an active trip, or build something in your library.
        </p>
      </header>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Active trips</h2>
          <Button asChild variant="ghost" size="sm">
            <Link to="/trips">
              All trips
              <ArrowRight />
            </Link>
          </Button>
        </div>

        {trips.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : trips.data && trips.data.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {trips.data.map((trip) => (
              <ActiveTripCard key={trip.id} trip={trip} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-[var(--border)] bg-[var(--card)] py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--accent)] text-[var(--accent-foreground)]">
              <Luggage className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="font-medium">No active trips</p>
              <p className="text-sm text-[var(--muted-foreground)]">
                Start a trip and pull in your packing lists.
              </p>
            </div>
            <Button asChild>
              <Link to="/trips">
                <Plus />
                New trip
              </Link>
            </Button>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Build your library</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <QuickLink
            to="/library"
            icon={<ListChecks className="h-5 w-5" />}
            title="Master Lists"
            description="Reusable packing checklists you pull into any trip."
          />
          <QuickLink
            to="/activities"
            icon={<Sparkles className="h-5 w-5" />}
            title="Activities"
            description="Bundle lists for the things you do on a trip."
          />
        </div>
      </section>
    </div>
  );
}

function ActiveTripCard({ trip }: { trip: Trip }) {
  const items = useTripItems(trip.id);
  const progress = items.data ? tripProgress(items.data) : null;

  return (
    <Link
      to="/trips/$tripId"
      params={{ tripId: trip.id }}
      search={{ view: 'overview' }}
      className="flex flex-col gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-sm transition-colors hover:bg-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate font-medium">{trip.name}</span>
        <ArrowRight className="h-4 w-4 shrink-0 text-[var(--muted-foreground)]" />
      </div>
      {progress && progress.total > 0 ? (
        <Progress label="Packed" value={progress.done} max={progress.total} />
      ) : (
        <p className="text-sm text-[var(--muted-foreground)]">Nothing packed yet</p>
      )}
    </Link>
  );
}

function QuickLink({
  to,
  icon,
  title,
  description,
}: {
  to: '/library' | '/activities';
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-sm transition-colors hover:bg-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent)] text-[var(--accent-foreground)]">
        {icon}
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{title}</span>
        <span className="text-sm text-[var(--muted-foreground)]">{description}</span>
      </span>
    </Link>
  );
}
